import { useEffect, useId, useRef } from 'react';
import { m } from 'motion/react';
import { CalendarClock, Check, Copy, ExternalLink, Flag, Heart, MapPin, Navigation, Plus, Star, Ticket, X, type LucideIcon } from 'lucide-react';
import type { Museum } from '../data/types';
import { useT } from '../hooks/useT';
import { afstandKm, euro, googleMapsUrl, googleMapsZoekUrl, isBezocht, type MuseumStatus } from '../utils/museum';
import { datumKort } from '../utils/datum';
import { chip } from '../utils/ui';
import { CATEGORIE_ICOON } from './categorie';

interface MuseumPaneelProps {
  museum: Museum;
  status: MuseumStatus | undefined;
  mijnPositie: { lat: number; lng: number } | null;
  onWissel: (wat: 'b' | 'w' | 'f') => void;
  onExtraBezoek: () => void;
  onKopieer: (tekst: string) => void;
  onClose: () => void;
}

const SCHAKELAARS: { wat: 'b' | 'w' | 'f'; label: 'bezocht' | 'wens' | 'fav'; Icon: LucideIcon; aan: string }[] = [
  { wat: 'b', label: 'bezocht', Icon: Check, aan: 'bg-emerald-700 text-white dark:bg-emerald-600' },
  { wat: 'w', label: 'wens', Icon: Flag, aan: 'bg-amber-700 text-white dark:bg-amber-600' },
  { wat: 'f', label: 'fav', Icon: Heart, aan: 'bg-rose-700 text-white dark:bg-rose-600' },
];

/**
 * Museumdetails. Mobiel: bottom sheet (veeg omlaag om te sluiten). Breder: zijpaneel rechts.
 * Niet-modaal: de kaart blijft bruikbaar terwijl het paneel open staat.
 */
export function MuseumPaneel({ museum, status, mijnPositie, onWissel, onExtraBezoek, onKopieer, onClose }: MuseumPaneelProps) {
  const { t, tf, language } = useT();
  const titelId = useId();
  const ref = useRef<HTMLElement>(null);
  const CatIcon = CATEGORIE_ICOON[museum.categorie];
  const bezoeken = status?.b ?? [];
  const adres = [museum.adres, [museum.postcode, museum.plaats].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Geen andere dialoog (menu, modal) erboven: dan sluit Escape het paneel.
      if (e.key === 'Escape' && !document.querySelector('[aria-modal="true"]')) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Focus naar het paneel bij openen of wisselen van museum (schermlezers horen de naam).
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, [museum.id]);

  return (
    <m.section
      ref={ref}
      key={museum.id}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titelId}
      tabIndex={-1}
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 40, opacity: 0 }}
      transition={{ duration: 0.2 }}
      drag="y"
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{ top: 0, bottom: 0.6 }}
      dragListener={typeof window !== 'undefined' && window.innerWidth < 768}
      onDragEnd={(_, info) => {
        if (info.offset.y > 100 || info.velocity.y > 600) onClose();
      }}
      className="fixed inset-x-0 bottom-0 z-30 max-h-[60dvh] overflow-y-auto rounded-t-3xl bg-slate-50/95 p-5 pb-8 text-slate-900 shadow-2xl outline-none backdrop-blur-md md:inset-x-auto md:bottom-4 md:right-4 md:top-4 md:max-h-none md:w-[400px] md:rounded-3xl md:pb-5 dark:border dark:border-white/10 dark:bg-slate-900/95 dark:text-slate-100"
    >
      <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-black/10 md:hidden dark:bg-white/20" aria-hidden />

      <div className="flex items-start justify-between gap-3">
        <span className="flex min-h-8 items-center gap-1.5 whitespace-nowrap rounded-full bg-indigo-100 px-3 text-xs font-semibold text-indigo-900 dark:bg-indigo-950/70 dark:text-indigo-200">
          <CatIcon size={14} aria-hidden /> {t(`cat_${museum.categorie}`)}
        </span>
        <button
          onClick={onClose}
          aria-label={t('sluitPaneel')}
          className="-mr-1 -mt-1 grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-black/5 transition-all duration-150 active:scale-95 dark:bg-slate-800/80"
        >
          <X size={20} aria-hidden />
        </button>
      </div>

      <h2 id={titelId} className="mt-2 text-2xl font-bold leading-tight">{museum.naam}</h2>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        {museum.plaats} · {museum.provincie}
        {mijnPositie && <> · {tf('afstand', { km: afstandKm(mijnPositie, museum).toFixed(afstandKm(mijnPositie, museum) < 10 ? 1 : 0) })}</>}
      </p>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {SCHAKELAARS.map(({ wat, label, Icon, aan }) => {
          const actief = wat === 'b' ? isBezocht(status) : !!status?.[wat];
          return (
            <button
              key={wat}
              onClick={() => onWissel(wat)}
              aria-pressed={actief}
              className={`flex min-h-14 flex-col items-center justify-center gap-1 rounded-2xl px-2 text-sm font-semibold transition-all duration-150 active:scale-95 ${
                actief ? aan : 'bg-black/5 dark:bg-slate-800/80'
              }`}
            >
              <Icon size={20} aria-hidden fill={actief && wat !== 'b' ? 'currentColor' : 'none'} />
              <span className="whitespace-nowrap">{t(label)}</span>
            </button>
          );
        })}
      </div>

      {bezoeken.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-emerald-100/80 py-1.5 pl-4 pr-1.5 text-sm text-emerald-950 dark:bg-emerald-950/60 dark:text-emerald-100">
          <span>
            {bezoeken.length === 1
              ? tf('eenBezoek', { datum: datumKort(bezoeken[0], language) })
              : tf('meerBezoeken', { n: bezoeken.length, datum: datumKort(bezoeken[bezoeken.length - 1], language) })}
          </span>
          <button onClick={onExtraBezoek} className={`${chip(false)} bg-white/60 dark:bg-slate-900/60`}>
            <Plus size={16} aria-hidden /> {t('nogEenBezoek')}
          </button>
        </div>
      )}

      {museum.omschrijving && (
        <p className="mt-3 leading-relaxed">
          {museum.omschrijving}
          {museum.wikipedia && (
            <>
              {' '}
              <a
                href={museum.wikipedia}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap text-sm text-slate-600 underline underline-offset-2 dark:text-slate-400"
              >
                {t('bronWikipedia')}
              </a>
            </>
          )}
        </p>
      )}

      <ul className="mt-2 text-sm">
        {adres && (
          <li className="-mx-2 flex items-center gap-1">
            <a
              href={googleMapsZoekUrl(museum)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${t('toonOpKaart')}: ${adres}`}
              className="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-2xl px-2 transition-all duration-150 hover:bg-black/5 active:scale-[0.98] dark:hover:bg-white/5"
            >
              <MapPin size={18} className="shrink-0 text-slate-600 dark:text-slate-400" aria-hidden />
              <span className="underline decoration-slate-400 underline-offset-2 dark:decoration-slate-500">{adres}</span>
            </a>
            <button
              onClick={() => onKopieer(adres)}
              aria-label={`${t('kopieerAdres')}: ${adres}`}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-slate-600 transition-all duration-150 hover:bg-black/5 active:scale-95 dark:text-slate-400 dark:hover:bg-white/5"
            >
              <Copy size={18} aria-hidden />
            </button>
          </li>
        )}
        {museum.beoordeling !== undefined && (
          <li className="flex min-h-9 items-center gap-3">
            <Star size={18} className="shrink-0 text-slate-600 dark:text-slate-400" aria-hidden />
            <span>
              {tf('beoordeling', {
                score: museum.beoordeling.toLocaleString(language === 'en' ? 'en-GB' : 'nl-NL', { minimumFractionDigits: 1 }),
                n: (museum.reviews ?? 0).toLocaleString(language === 'en' ? 'en-GB' : 'nl-NL'),
              })}
            </span>
          </li>
        )}
        {museum.prijs !== undefined && (
          <li className="flex min-h-9 items-center gap-3">
            <Ticket size={18} className="shrink-0 text-slate-600 dark:text-slate-400" aria-hidden />
            <span>{tf('prijsLabel', { prijs: euro(museum.prijs, language) })}</span>
          </li>
        )}
        {museum.reserveren && (
          <li className="flex min-h-9 items-center gap-3 font-semibold text-amber-800 dark:text-amber-300">
            <CalendarClock size={18} className="shrink-0" aria-hidden />
            <span>{t('reserverenLabel')}</span>
          </li>
        )}
      </ul>

      <div className="mt-4 flex gap-2">
        <a
          href={googleMapsUrl(museum)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-h-12 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-indigo-700 px-4 font-semibold text-white transition-all duration-150 active:scale-95"
        >
          <Navigation size={18} aria-hidden /> {t('navigeer')}
        </a>
        {museum.website && (
          <a
            href={museum.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-12 items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-black/5 px-4 font-semibold transition-all duration-150 active:scale-95 dark:bg-slate-800/80"
          >
            <ExternalLink size={18} aria-hidden /> {t('website')}
          </a>
        )}
      </div>
    </m.section>
  );
}
